import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { ConfirmDialog } from '~/components/confirm-dialog'
import { Dialog } from '~/components/dialog'
import { ExplorePanel } from '~/components/explore-dialog'
import { FeedbackPanel } from '~/components/feedback-panel'
import { PanelCard } from '~/components/panel-card'
import { PanelHeader } from '~/components/panel-header'
import { SafeImage } from '~/components/safe-image'
import { SettingsPanel } from '~/components/settings-panel'
import { Skeleton } from '~/components/skeleton'
import { TemplatePanel } from '~/components/template-panel'
import { TemplatesPanel } from '~/components/templates-panel'
import { reached, removed } from '~/components/use-work'
import { WeighInAddPhotoPanel } from '~/components/weighin-add-photo-panel'
import { WeighInWeightPanel } from '~/components/weighin-weight-panel'
import { useApi, useDataState } from '~/data/data-context'
import type { ExploreQuery } from '~/data/templates'
import { photoName, todayKey, type WeighInPhoto } from '~/data/weighins'
import { useWeighIns, useWeighInWriter } from '~/data/weighins-store'
import {
  accountPanelOf,
  DIALOG_PATHS,
  settingsSectionOf,
  type DialogName,
  type SettingsSection,
} from '~/lib/dialogs'
import { lastPointer } from '~/lib/last-pointer'

/** Matches the leave animation in `app.css`, after which the card is let go of. */
const LEAVE_MS = 120

/** What the dialog calls itself while a settings section is the thing it is showing. */
const SECTION_TITLES: Record<SettingsSection, string> = {
  units: 'Units',
  videos: 'Videos',
  weighIn: 'Weigh-in',
  nutrition: 'Nutrition',
  storage: 'Storage',
  data: 'Data',
  'delete-account': 'Delete account',
  export: 'Export your data',
  import: 'Import from another app',
}

/**
 * The pages the account used to reach through a menu in the corner — the templates page,
 * Explore, a template's page, settings and its sections, feedback, and the weigh-in panels.
 * They open centred over the page they were opened from, the way every other page over a page
 * does, per `/specs/app.spec.md`: there is one kind of page over a page and not two.
 *
 * Each rides the URL exactly as it always has, and each panel's own back walks it home to
 * whatever it was reached from — or out of the dialog, where it was reached from nothing.
 */
export function AccountPanels({
  name,
  day: askedDay,
  measure,
  explore,
  template,
  onClose,
}: {
  /** The dialog the URL names, which is one of the panels or one of settings' own sections. */
  name: DialogName
  /** The day a weigh-in panel is on, or null for today. */
  day: string | null
  /**
   * Which reading a weigh-in panel opens on — `weight`, or a measurement's name — or null to
   * open on the weight as it always has. It is what a pill on a day's heading carries: pressing
   * the waist means wanting to change the waist, not wanting to read the form from the top.
   */
  measure: string | null
  /** What Explore is showing, which its panel and a template's page both read. */
  explore: ExploreQuery
  /** Which template the template panel is about, or null while none is open. */
  template: string | null
  onClose: () => void
}) {
  const api = useApi()
  const navigate = useNavigate()
  const state = useDataState()

  /*
   * A settings section is a page of settings rather than a panel of its own, so the name says
   * two things: which panel is showing, and — where that panel is settings — which of its
   * sections it is open on.
   */
  const section = settingsSectionOf(name)
  const panel = accountPanelOf(name) ?? 'settings'

  // The weigh-ins, shared with the timeline: a save here is on the page in the same breath.
  const weighIns = useWeighIns()
  const weighInWriter = useWeighInWriter()
  const day = askedDay ?? todayKey()
  const dayWeighIn = (weighIns ?? []).find((weighIn) => weighIn.day === day)

  const [origin] = useState(lastPointer)
  /*
   * On its way out: the card plays its leave and the address is let go of once that has run,
   * so what closes it is one movement rather than a card vanishing and a page catching up.
   */
  const [leaving, setLeaving] = useState(false)
  const dismiss = () => {
    if (leaving) {
      return
    }
    setLeaving(true)
    window.setTimeout(onClose, LEAVE_MS)
  }
  const [showingPhoto, setShowingPhoto] = useState<WeighInPhoto | null>(null)
  const [removingPhoto, setRemovingPhoto] = useState<WeighInPhoto | null>(null)

  /** Removed on the server first; refused, the confirmation stays and says so. */
  const removePhoto = async (photo: WeighInPhoto) => {
    await reached(() => removed(() => api.deleteWeighInPhoto(photo.id)))
    weighInWriter.dropPhoto(photo)
  }

  /** Where adding a photo ends: back on the weigh-in it belongs to, in place of the add step. */
  const toWeighIn = () => {
    void navigate({
      to: '.',
      search: (prev) => ({ ...prev, dialog: 'weighin-weight' as const }),
      mask: { to: DIALOG_PATHS['weighin-weight'] },
      replace: true,
      resetScroll: false,
      viewTransition: false,
    })
  }

  /** Feedback is reached from settings, so its way back is settings — see the feedback spec. */
  const toSettings = () => {
    void navigate({
      to: '.',
      search: (prev) => ({ ...prev, dialog: 'settings' as const }),
      mask: { to: DIALOG_PATHS.settings },
      replace: true,
      resetScroll: false,
      viewTransition: false,
    })
  }

  /*
   * A template's page is about one template, so the dialog is named for it. Your own is in the
   * library already; somebody else's is named by the panel once it has been fetched.
   */
  const held = state.templates.find((candidate) => candidate.id === template)
  const [namedTemplate, setNamedTemplate] = useState<{ id: string; name: string } | null>(null)
  const templateName = held?.name ?? (namedTemplate?.id === template ? namedTemplate.name : null)

  const label =
    panel === 'templates'
      ? 'Templates'
      : panel === 'explore'
        ? 'Explore'
        : panel === 'template'
          ? (templateName ?? 'Template')
          : panel === 'settings' || section !== null
            ? section === null
              ? 'Settings'
              : SECTION_TITLES[section]
            : panel === 'feedback'
              ? 'Feedback'
              : panel === 'weighin-weight'
                ? 'Weigh-in'
                : 'Add a progress photo'

  return (
    <>
      <PanelCard
        label={label}
        // Which panel is showing, which is not what it is called: a template's page is called
        // "Template" until its name arrives, and the card must not be rebuilt when it does.
        showing={`${panel}:${section ?? ''}:${template ?? ''}`}
        // Where it was opened from, taken once: the press that opened it is the press that
        // just happened, and a later one — inside the card — is not what it came out of.
        at={origin}
        leaving={leaving}
        takeFocus
        onDismiss={dismiss}
      >
        {panel === 'templates' ? (
          <TemplatesPanel onBack={dismiss} />
        ) : panel === 'explore' ? (
          <ExplorePanel query={explore} />
        ) : panel === 'template' ? (
          // Keyed by which template, so walking from one to another swaps the panel rather
          // than leaving the first one's state standing in the second.
          <TemplatePanel
            key={template ?? ''}
            templateId={template ?? ''}
            onName={(name) => {
              setNamedTemplate((was) =>
                was?.id === template && was.name === name ? was : { id: template ?? '', name },
              )
            }}
          />
        ) : panel === 'feedback' ? (
          <FeedbackPanel onBack={toSettings} />
        ) : panel === 'weighin-weight' ? (
          weighIns === null ? (
            <PanelLoading title="Measurements" onClose={dismiss} />
          ) : (
            <WeighInWeightPanel
              key={day}
              day={day}
              opensOn={measure}
              weighIn={dayWeighIn}
              onSaved={weighInWriter.keep}
              onShowPhoto={setShowingPhoto}
              onRemovePhoto={setRemovingPhoto}
              onBack={dismiss}
            />
          )
        ) : panel === 'weighin-add-photo' ? (
          <WeighInAddPhotoPanel
            day={day}
            onAdded={(photo) => {
              weighInWriter.keepPhoto(photo)
              toWeighIn()
            }}
            onBack={toWeighIn}
          />
        ) : (
          <SettingsPanel section={section} onBack={dismiss} />
        )}
      </PanelCard>

      {/* A photo, as big as the window gives it. */}
      {showingPhoto !== null && (
        <Dialog
          label={photoName(showingPhoto)}
          onDismiss={() => {
            setShowingPhoto(null)
          }}
          className="max-w-4xl bg-black p-2"
        >
          <SafeImage
            src={api.weighInPhotoUrl(showingPhoto.id)}
            alt={photoName(showingPhoto)}
            // The frame takes the rounding and the black: left bare it is a square grey box
            // behind a rounded photo, and `object-contain` letterboxes onto grey.
            frameClassName="rounded-2xl bg-black"
            className="max-h-[85dvh] w-full rounded-2xl object-contain"
          />
        </Dialog>
      )}

      {removingPhoto !== null && (
        <ConfirmDialog
          title="Remove this photo?"
          body={`The ${photoName(removingPhoto).toLowerCase()} photo goes for good.`}
          confirm="Remove"
          onConfirm={async () => {
            await removePhoto(removingPhoto)
            setRemovingPhoto(null)
          }}
          onDismiss={() => {
            setRemovingPhoto(null)
          }}
        />
      )}
    </>
  )
}

/** The shape of a weigh-in panel still waiting on the day's data. */
function PanelLoading({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <>
      <PanelHeader title={title} close onBack={onClose} />
      <div
        role="status"
        aria-label={`Loading ${title.toLowerCase()}`}
        className="w-80 space-y-3 px-4 pb-4"
      >
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    </>
  )
}
